/** 有设备申请连接时，顶部出现一条提示，核对配对码后确认或拒绝（在本机或已配对的设备上显示）。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Smartphone } from "lucide-react";
import { errorMessage } from "@/shared/api/client";
import { decideDevice, deviceList, me } from "@/shared/api/gen/sdk.gen";
import { Button, ErrorNote } from "@/shared/ui";

export const deviceKeys = { me: ["devices", "me"] as const, list: ["devices", "list"] as const };

export function useDevices() {
  const self = useQuery({
    queryKey: deviceKeys.me,
    queryFn: async () => (await me()).data,
    staleTime: Infinity,
  });
  const local = self.data?.status === "local";
  // 本机，或已配对的设备（就是你自己）：可以确认新设备、撤销旧设备
  const owner = local || self.data?.status === "active";
  const list = useQuery({
    queryKey: deviceKeys.list,
    queryFn: async () => (await deviceList()).data,
    enabled: owner,
  });
  return { local, owner, selfId: self.data?.id ?? "", devices: list.data ?? [] };
}

export function PairingRequests() {
  const qc = useQueryClient();
  const { owner, devices } = useDevices();
  const decide = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) =>
      (await decideDevice({ path: { device_id: id }, body: { approve } })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: deviceKeys.list }),
  });
  const pending = devices.filter((d) => d.status === "pending");
  if (!owner || !pending.length) return null;
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 border-b border-signal-line bg-signal-soft px-4 py-2.5 text-[13.5px]"
    >
      {pending.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Smartphone className="size-4 text-signal-ink" />
          <span>
            “{d.name}”申请连接你的朝夕，配对码 <b className="font-mono tracking-widest">{d.code}</b>
            。和设备上显示的一致才确认。
          </span>
          <span className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="signal"
              busy={decide.isPending}
              onClick={() => decide.mutate({ id: d.id, approve: true })}
            >
              确认
            </Button>
            <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: d.id, approve: false })}>
              拒绝
            </Button>
          </span>
        </div>
      ))}
      {decide.error ? <ErrorNote>{errorMessage(decide.error)}</ErrorNote> : null}
    </div>
  );
}
